import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/* ─── Compositional building blocks (shadcn-style) ─── */

export const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<"nav"> & { separator?: React.ReactNode }
>(function Breadcrumb({ className, ...props }, ref) {
  return (
    <nav
      ref={ref}
      aria-label="breadcrumb"
      className={cx("cw-v2-breadcrumb", className)}
      {...props}
    />
  );
});
Breadcrumb.displayName = "Breadcrumb";

export const BreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<"ol">
>(function BreadcrumbList({ className, ...props }, ref) {
  return <ol ref={ref} className={cx("cw-v2-breadcrumb__list", className)} {...props} />;
});
BreadcrumbList.displayName = "BreadcrumbList";

export const BreadcrumbItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<"li">>(
  function BreadcrumbItem({ className, ...props }, ref) {
    return <li ref={ref} className={cx("cw-v2-breadcrumb__item", className)} {...props} />;
  },
);
BreadcrumbItem.displayName = "BreadcrumbItem";

export const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<"a"> & { asChild?: boolean }
>(function BreadcrumbLink({ asChild, className, ...props }, ref) {
  const Comp = asChild ? Slot : "a";
  return <Comp ref={ref} className={cx("cw-v2-breadcrumb__link", className)} {...props} />;
});
BreadcrumbLink.displayName = "BreadcrumbLink";

export const BreadcrumbPage = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<"span">
>(function BreadcrumbPage({ className, ...props }, ref) {
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: BreadcrumbPage represents the current page (aria-disabled="true", aria-current="page") — it is intentionally not a tab-stop.
    <span
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cx("cw-v2-breadcrumb__page", className)}
      {...props}
    />
  );
});
BreadcrumbPage.displayName = "BreadcrumbPage";

export function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      role="presentation"
      aria-hidden
      className={cx("cw-v2-breadcrumb__separator", className)}
      {...props}
    >
      {children ?? <ChevronRight className="cw-v2-breadcrumb__separator-icon" />}
    </li>
  );
}

export function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      role="presentation"
      aria-hidden
      className={cx("cw-v2-breadcrumb__ellipsis", className)}
      {...props}
    >
      <MoreHorizontal className="cw-v2-breadcrumb__ellipsis-icon" />
      <span className="cw-v2-breadcrumb__ellipsis-sr">More</span>
    </span>
  );
}

/* ─── Convenience: data-driven BreadcrumbTrail ─── */

export interface BreadcrumbCrumb {
  /** Display label. */
  label: React.ReactNode;
  /** href for an anchor render. Omit for the current page (last item). */
  href?: string;
  /** Element override; e.g. a <Link> from your router. Used with asChild semantics. */
  render?: (props: { className: string; children: React.ReactNode }) => React.ReactElement;
}

export interface BreadcrumbTrailProps {
  className?: string;
  /** Ordered list of crumbs from root to current page. */
  items: BreadcrumbCrumb[];
  /**
   * Maximum number of items to render. When the list is longer, the middle is
   * collapsed to an ellipsis (root + ellipsis + last items shown).
   * Default: render all items.
   */
  maxItems?: number;
  /** Override the separator between items. Defaults to a chevron. */
  separator?: React.ReactNode;
  /**
   * Accessible name for the breadcrumb landmark. Defaults to `"breadcrumb"`.
   * Override when multiple breadcrumb trails coexist on the same page so
   * each navigation landmark has a unique name.
   */
  "aria-label"?: string;
}

/**
 * Data-driven breadcrumb. The last item is always the current page (rendered
 * as plain text); earlier items are rendered as links.
 */
export function BreadcrumbTrail({
  className,
  items,
  maxItems,
  separator,
  "aria-label": ariaLabel,
}: BreadcrumbTrailProps) {
  if (items.length === 0) return null;

  const renderCrumbContent = (crumb: BreadcrumbCrumb, isCurrent: boolean) => {
    if (isCurrent) {
      return <BreadcrumbPage>{crumb.label}</BreadcrumbPage>;
    }
    if (crumb.render) {
      return (
        <BreadcrumbLink asChild>
          {crumb.render({ className: "", children: crumb.label })}
        </BreadcrumbLink>
      );
    }
    return <BreadcrumbLink href={crumb.href}>{crumb.label}</BreadcrumbLink>;
  };

  const shouldCollapse = typeof maxItems === "number" && items.length > maxItems && maxItems >= 2;
  const renderItems: Array<{ key: string; node: React.ReactNode }> = [];

  if (!shouldCollapse) {
    items.forEach((crumb, index) => {
      const isCurrent = index === items.length - 1;
      renderItems.push({
        key: `crumb-${index}`,
        node: <BreadcrumbItem>{renderCrumbContent(crumb, isCurrent)}</BreadcrumbItem>,
      });
      if (!isCurrent) {
        renderItems.push({
          key: `sep-${index}`,
          node: <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>,
        });
      }
    });
  } else {
    const tailCount = Math.max(1, (maxItems as number) - 1);
    const tail = items.slice(-tailCount);
    const head = items[0];
    if (!head) return null;

    renderItems.push({
      key: "crumb-head",
      node: <BreadcrumbItem>{renderCrumbContent(head, false)}</BreadcrumbItem>,
    });
    renderItems.push({
      key: "sep-head",
      node: <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>,
    });
    renderItems.push({
      key: "ellipsis",
      node: (
        <BreadcrumbItem>
          <BreadcrumbEllipsis />
        </BreadcrumbItem>
      ),
    });
    renderItems.push({
      key: "sep-ellipsis",
      node: <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>,
    });
    tail.forEach((crumb, indexInTail) => {
      const itemIndex = items.length - tail.length + indexInTail;
      const isCurrent = itemIndex === items.length - 1;
      renderItems.push({
        key: `crumb-tail-${indexInTail}`,
        node: <BreadcrumbItem>{renderCrumbContent(crumb, isCurrent)}</BreadcrumbItem>,
      });
      if (!isCurrent) {
        renderItems.push({
          key: `sep-tail-${indexInTail}`,
          node: <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>,
        });
      }
    });
  }

  // Only forward aria-label when a consumer overrides it; passing undefined
  // would erase Breadcrumb's default "breadcrumb" label via prop spread.
  const labelProps = ariaLabel === undefined ? {} : { "aria-label": ariaLabel };
  return (
    <Breadcrumb className={className} {...labelProps}>
      <BreadcrumbList>
        {renderItems.map((entry) => (
          <React.Fragment key={entry.key}>{entry.node}</React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
